"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  User as FirebaseUser,
  onIdTokenChanged,
  signInWithCustomToken,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { getFirebaseAuth, getGoogleProvider } from "@/lib/firebase/client";
import { sessionTimeRemainingMs } from "@/lib/firebase/session-policy";
import { appGetItem, appSetItem, setStorageAuthState } from "@/lib/auth-storage";
import { primeIdTokenCache } from "@/lib/api-client";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthUser {
  /** Firebase uid. */
  id: string;
  email: string | null;
  name: string | null;
  image: string | null;
}

/** School/institution and country. Both optional — see requirement 3. */
export interface UserProfile {
  institution: string | null;
  country: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  status: AuthStatus;
  /** Signed-in user's profile, or null until it has been fetched. */
  profile: UserProfile | null;
  /**
   * True when this login should ask for the school/institution: the user is
   * signed in, has not recorded one, and has not skipped the prompt during
   * this login.
   */
  needsProfilePrompt: boolean;
  signInWithGoogle: (profile?: Partial<UserProfile>) => Promise<void>;
  /** Exchange a verified OTP for a Firebase session. */
  signInWithOtp: (
    email: string,
    code: string,
    profile?: Partial<UserProfile>
  ) => Promise<void>;
  /** Save the profile fields the user filled in; blank ones are ignored. */
  saveProfile: (profile: Partial<UserProfile>) => Promise<void>;
  /** Skip the prompt for this login only; it returns on the next one. */
  dismissProfilePrompt: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Remembers which login the user skipped the institution prompt on, keyed by
 * the sign-in time. A fresh login has a new sign-in time, so the prompt comes
 * back — exactly what requirement 3 asks for.
 */
const PROMPT_SKIPPED_KEY = "jupas_profile_prompt_skipped";

function readSkippedAuthTime(): number | null {
  try {
    const raw = appGetItem(PROMPT_SKIPPED_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function toAuthUser(user: FirebaseUser): AuthUser {
  return {
    id: user.uid,
    email: user.email,
    name: user.displayName ?? user.email?.split("@")[0] ?? null,
    image: user.photoURL,
  };
}

function cleanProfile(profile?: Partial<UserProfile>) {
  return {
    institution: profile?.institution?.trim() || undefined,
    country: profile?.country?.trim() || undefined,
  };
}

/**
 * Tell the server a user has signed in, so it can create or link their record,
 * bump the login count and store any profile fields from the sign-up form.
 * Failure is non-fatal — the API routes create the record lazily on first
 * authenticated request.
 */
async function recordSignIn(
  user: FirebaseUser,
  profile?: Partial<UserProfile>
): Promise<void> {
  try {
    const token = await user.getIdToken();
    await fetch("/api/auth/session", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cleanProfile(profile)),
    });
  } catch (error) {
    console.error("Failed to record sign-in:", error);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [authTime, setAuthTime] = useState<number | null>(null);
  const [promptSkipped, setPromptSkipped] = useState(false);
  const expiryTimer = useRef<number | null>(null);

  const signOut = useCallback(async () => {
    await firebaseSignOut(getFirebaseAuth());
  }, []);

  useEffect(() => {
    // onIdTokenChanged (rather than onAuthStateChanged) also fires on token
    // refresh, keeping us in step with the token authedFetch will send.
    const unsubscribe = onIdTokenChanged(
      getFirebaseAuth(),
      async (firebaseUser) => {
        if (!firebaseUser) {
          primeIdTokenCache(null);
          setUser(null);
          setAuthTime(null);
          setProfile(null);
          setStatus("unauthenticated");
          return;
        }

        setUser(toAuthUser(firebaseUser));
        setStatus("authenticated");
        try {
          const result = await firebaseUser.getIdTokenResult();
          // This callback also fires on every background refresh, so it is the one place that
          // sees every token the SDK issues — which is what lets a save dispatch its request
          // without awaiting one. See authedFetchNow.
          primeIdTokenCache(result.token);
          setAuthTime(Date.parse(result.authTime) / 1000);
        } catch (error) {
          console.error("Failed to read sign-in time:", error);
        }
      }
    );
    return unsubscribe;
  }, []);

  // Enforce the one-day login window in the browser. The API routes enforce the
  // same cap independently, so a tab that misses this still cannot write data.
  useEffect(() => {
    if (expiryTimer.current !== null) {
      window.clearTimeout(expiryTimer.current);
      expiryTimer.current = null;
    }
    if (status !== "authenticated" || authTime === null) return;

    const remaining = sessionTimeRemainingMs(authTime);
    if (remaining === 0) {
      void signOut();
      return;
    }
    expiryTimer.current = window.setTimeout(() => {
      void signOut();
    }, remaining);

    return () => {
      if (expiryTimer.current !== null) {
        window.clearTimeout(expiryTimer.current);
        expiryTimer.current = null;
      }
    };
  }, [status, authTime, signOut]);

  // Application data stays in memory for both signed-in and anonymous sessions.
  useEffect(() => {
    if (status === "loading") return;
    setStorageAuthState(status === "authenticated");
  }, [status]);

  // Was the prompt already skipped during *this* login?
  useEffect(() => {
    if (status !== "authenticated" || authTime === null) {
      setPromptSkipped(false);
      return;
    }
    setPromptSkipped(readSkippedAuthTime() === Math.floor(authTime));
  }, [status, authTime]);

  // Load the profile so we know whether to prompt for the institution.
  // Keyed by uid, not the user object, so an hourly token refresh does not
  // trigger a redundant fetch.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (status !== "authenticated" || !userId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseAuth().currentUser?.getIdToken();
        if (!token) return;
        const response = await fetch("/api/user/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as UserProfile;
        if (!cancelled) {
          setProfile({
            institution: data.institution ?? null,
            country: data.country ?? null,
          });
        }
      } catch (error) {
        console.error("Failed to load user profile:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, userId]);

  const signInWithGoogle = useCallback(async (profileInput?: Partial<UserProfile>) => {
    const auth = getFirebaseAuth();
    try {
      const credential = await signInWithPopup(auth, getGoogleProvider());
      await recordSignIn(credential.user, profileInput);
    } catch (error) {
      const code = (error as { code?: string })?.code;
      if (code === "auth/popup-blocked" || code === "auth/popup-closed-by-user") {
        // Fall back to a full-page redirect; recordSignIn runs on return via
        // the lazy path in getAuthedUser.
        await signInWithRedirect(auth, getGoogleProvider());
        return;
      }
      throw error;
    }
  }, []);

  const signInWithOtp = useCallback(
    async (email: string, code: string, profileInput?: Partial<UserProfile>) => {
      const response = await fetch("/api/auth/otp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });

      const data = (await response.json()) as {
        customToken?: string;
        error?: string;
      };

      if (!response.ok || !data.customToken) {
        throw new Error(data.error || "Verification failed");
      }

      const credential = await signInWithCustomToken(
        getFirebaseAuth(),
        data.customToken
      );
      await recordSignIn(credential.user, profileInput);
    },
    []
  );

  const saveProfile = useCallback(async (profileInput: Partial<UserProfile>) => {
    const token = await getFirebaseAuth().currentUser?.getIdToken();
    if (!token) throw new Error("Not signed in");

    const response = await fetch("/api/user/profile", {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cleanProfile(profileInput)),
    });

    if (!response.ok) {
      throw new Error("Failed to save profile");
    }

    const data = (await response.json()) as UserProfile;
    setProfile({
      institution: data.institution ?? null,
      country: data.country ?? null,
    });
  }, []);

  const dismissProfilePrompt = useCallback(() => {
    setPromptSkipped(true);
    if (authTime === null) return;
    appSetItem(PROMPT_SKIPPED_KEY, String(Math.floor(authTime)));
  }, [authTime]);

  const needsProfilePrompt =
    status === "authenticated" &&
    profile !== null &&
    !profile.institution &&
    !promptSkipped;

  const value = useMemo(
    () => ({
      user,
      status,
      profile,
      needsProfilePrompt,
      signInWithGoogle,
      signInWithOtp,
      saveProfile,
      dismissProfilePrompt,
      signOut,
    }),
    [
      user,
      status,
      profile,
      needsProfilePrompt,
      signInWithGoogle,
      signInWithOtp,
      saveProfile,
      dismissProfilePrompt,
      signOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
